import React, { useContext, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { getRustBuildInfo, indexServers } from "../http/ServerAPI";
import { usePollingData } from "../hooks/usePollingData";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import AddCircle from "@mui/icons-material/AddCircle";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import UpdateOutlined from "@mui/icons-material/UpdateOutlined";
import DnsOutlined from "@mui/icons-material/DnsOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import WarningAmber from "@mui/icons-material/WarningAmber";
import BreadCrumbs from "../components/Breadcrumbs";
import { Context } from "../index";
import ServerCard from "../components/ServerCard";
import { CREATE_SERVER } from "../utils/consts";

const normalizeBuildInfo = (response) => {
    if (!response) return null;
    return response.data || response;
};

const formatDateTime = (value) => {
    if (!value) return "Неизвестно";

    const date = new Date(String(value).replace(" ", "T"));

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const getBuildStatus = (servers, latestBuildId) => {
    const serversWithBuild = servers.filter(server => Number(server.build_id) > 0);

    if (!latestBuildId || serversWithBuild.length === 0) {
        return {
            checked: serversWithBuild.length,
            upToDate: 0,
            outdated: 0,
            unknown: servers.length - serversWithBuild.length,
        };
    }

    const upToDate = serversWithBuild.filter(server => Number(server.build_id) >= Number(latestBuildId)).length;

    return {
        checked: serversWithBuild.length,
        upToDate,
        outdated: serversWithBuild.length - upToDate,
        unknown: servers.length - serversWithBuild.length,
    };
};

const ServersOverview = ({ servers, buildInfo, loadingBuildInfo, buildInfoError, reloadBuildInfo }) => {
    const runningCount = servers.filter(server => server.status_id === 3).length;
    const stoppedCount = servers.filter(server => server.status_id === 4 || server.status_id === 8).length;
    const loadingCount = servers.filter(server => server.status_id === 5).length;
    const latestBuildId = buildInfo?.build_id || buildInfo?.buildId || null;
    const buildStatus = useMemo(() => getBuildStatus(servers, latestBuildId), [servers, latestBuildId]);

    return (
        <Card
            elevation={0}
            sx={{
                mt: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                background: "linear-gradient(135deg, rgba(25,118,210,0.08), rgba(3,169,244,0.03))",
            }}
        >
            <CardContent>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    spacing={2}
                >
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <InfoOutlined color="primary" />
                            <Typography variant="h6">Общая информация</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                            Сводка по серверам и актуальному билду Rust.
                        </Typography>
                    </Box>

                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={loadingBuildInfo ? <CircularProgress size={16} /> : <UpdateOutlined />}
                        onClick={() => reloadBuildInfo().catch(() => {})}
                        disabled={loadingBuildInfo}
                    >
                        Обновить билд
                    </Button>
                </Stack>

                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Серверы
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap">
                                <DnsOutlined fontSize="small" color="action" />
                                <Typography variant="h5">{servers.length}</Typography>
                                <Chip size="small" color="success" label={`Online: ${runningCount}`} />
                                {loadingCount > 0 && <Chip size="small" color="info" label={`Loading: ${loadingCount}`} />}
                                {stoppedCount > 0 && <Chip size="small" variant="outlined" label={`Stopped: ${stoppedCount}`} />}
                            </Stack>
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Актуальный build Rust
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                <Typography variant="h5">
                                    {latestBuildId || "—"}
                                </Typography>
                                {buildInfo?.branch && (
                                    <Chip size="small" variant="outlined" label={buildInfo.branch} />
                                )}
                            </Stack>
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Дата выхода build
                            </Typography>
                            <Typography variant="body1" mt={0.8}>
                                {formatDateTime(buildInfo?.released_at || buildInfo?.releasedAt)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Соответствие серверов
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" mt={0.8} flexWrap="wrap">
                                {buildStatus.outdated > 0 ? (
                                    <Tooltip title="Есть серверы с build_id ниже актуального">
                                        <Chip
                                            size="small"
                                            color="warning"
                                            icon={<WarningAmber />}
                                            label={`Устарели: ${buildStatus.outdated}`}
                                        />
                                    </Tooltip>
                                ) : (
                                    <Chip
                                        size="small"
                                        color="success"
                                        icon={<CheckCircleOutline />}
                                        label={`Актуальны: ${buildStatus.upToDate}`}
                                    />
                                )}
                                {buildStatus.unknown > 0 && (
                                    <Chip size="small" variant="outlined" label={`Без build: ${buildStatus.unknown}`} />
                                )}
                            </Stack>
                        </Box>
                    </Grid>
                </Grid>

                {buildInfo?.notes && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {buildInfo.notes}
                    </Alert>
                )}

                {buildInfoError && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Не удалось загрузить информацию об актуальном билде Rust. Список серверов работает без неё.
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

const Servers = observer(() => {
    const navigate = useNavigate();
    const { user } = useContext(Context);
    const { data: servers = [], reload: loadServers } = usePollingData(indexServers, {
        initialData: [],
        intervalMs: 30000,
        onSuccess: (data) => user.setServers(data),
        errorMessage: "Ошибка загрузки серверов",
    });

    const {
        data: buildInfo,
        loading: loadingBuildInfo,
        error: buildInfoError,
        reload: reloadBuildInfo,
    } = usePollingData(getRustBuildInfo, {
        initialData: null,
        intervalMs: 5 * 60 * 1000,
        mapData: normalizeBuildInfo,
        errorMessage: "Ошибка загрузки информации о билде Rust",
    });

    const latestRustBuildId = buildInfo?.build_id || buildInfo?.buildId || null;

    return (
        <Container maxWidth="xl">
            <Box mt={3}>
                <BreadCrumbs hrefs="" name="Список серверов" />
            </Box>
            <Box mt={3} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Typography variant="h4">Сервера</Typography>
                <Button variant="contained" startIcon={<AddCircle />} onClick={() => navigate(CREATE_SERVER)}>
                    Создать сервер
                </Button>
            </Box>

            <ServersOverview
                servers={servers}
                buildInfo={buildInfo}
                loadingBuildInfo={loadingBuildInfo}
                buildInfoError={buildInfoError}
                reloadBuildInfo={reloadBuildInfo}
            />

            <Grid container spacing={2} mt={2}>
                {servers.map(server => (
                    <Grid item xs={12} sm={6} md={4} key={server.server_id}>
                        <ServerCard server={server} refreshServers={loadServers} latestRustBuildId={latestRustBuildId} />
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
});

export default Servers;
